/**
 * CR405: Safe string utilities to prevent toLowerCase/toUpperCase crashes
 * 
 * Use these helpers anywhere you need to call string methods on potentially
 * undefined, null, or non-string values.
 * 
 * USAGE: Import and use instead of raw .toLowerCase(), .toUpperCase(), .trim()
 * on any variable that could be undefined/null.
 */

/**
 * Safely convert a value to lowercase string.
 * Returns empty string if input is not a string.
 */
export const safeLower = (v: unknown): string => 
  (typeof v === "string" ? v.toLowerCase() : "");

/**
 * Safely convert a value to uppercase string.
 * Returns empty string if input is not a string.
 */
export const safeUpper = (v: unknown): string => 
  (typeof v === "string" ? v.toUpperCase() : "");

/**
 * Safely trim a value.
 * Returns empty string if input is not a string.
 */
export const safeTrim = (v: unknown): string => 
  (typeof v === "string" ? v.trim() : "");

/**
 * Safely coerce a value to string with fallback.
 * Returns the fallback if input is not a string.
 */
export const safeStr = (v: unknown, fallback = ""): string => 
  (typeof v === "string" ? v : fallback);

/**
 * Safely check if a string is non-empty after trimming.
 */
export const isNonEmptyString = (v: unknown): v is string =>
  typeof v === "string" && v.trim().length > 0;

/**
 * Safely trim and convert to uppercase. Useful for deal type keys.
 */
export const safeUpperTrim = (v: unknown, fallback = ""): string => {
  if (typeof v !== "string") return fallback;
  const trimmed = v.trim();
  return trimmed ? trimmed.toUpperCase() : fallback;
};

/**
 * Check if a string looks like a UUID (any version).
 * Accepts standard 8-4-4-4-12 hex format, case-insensitive.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isUuidLike = (v: unknown): v is string =>
  typeof v === "string" && UUID_RE.test(v);

/**
 * Safely extract hostname from a URL string.
 * Returns a truncated fallback if the URL is malformed.
 */
export const safeHostname = (url: unknown, maxFallbackLen = 30): string => {
  if (typeof url !== "string" || !url) return "-";
  try {
    // If relative, parse against a dummy base so we still get a hostname
    const u = url.startsWith("http") ? new URL(url) : new URL(url, "https://example.com");
    return url.startsWith("http") ? u.hostname : "(relative)";
  } catch {
    return url.length > maxFallbackLen ? url.slice(0, maxFallbackLen) + "…" : url;
  }
};
